import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Leaf, Sparkles, Layers } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { featuredProducts } from "../data/products";
import * as productsApi from "../api/productsApi";
import { resolveImageUrl } from "../utils/images";
import { useLanguage } from "../context/LanguageContext";
import { updateSEO } from "../utils/seo";
import "./Home.css";

function normalizeApiProduct(p, isRTL) {
  const sortedImages = (p.productImages || []).slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  return {
    id: p.id,
    name: p.name,
    arabicName: p.arabicName,
    arabicDescription: p.arabicDescription,
    description: p.description,
    price: Number(p.basePrice),
    imageUrl: p.thumbnailImage || sortedImages[0]?.url,
    color: p.colorVariants?.[0]?.colorHex || "#d8cfc0",
    borderColor: "#c9a96e",
    badge: (p.unitsSold && p.unitsSold > 0) ? (isRTL ? "الأكثر مبيعاً" : "Best Seller") : null,
    unitsSold: p.unitsSold || 0,
    source: "api",
  };
}

export default function Home({ onAddToCart }) {
  const { t, isRTL } = useLanguage();
  const [featured, setFeatured] = useState(featuredProducts);
  const [dbCollections, setDbCollections] = useState([]);

  const collections = [
    {
      id: "baby",
      title: t("home.collBaby"),
      subtitle: t("home.collBabySub"),
      description: isRTL ? "مُصممة خصيصاً لبشرة الأطفال الحساسة" : "Specially crafted for delicate skin",
      color: "#d4c4a8",
      link: "/collections/baby",
    },
    {
      id: "towels",
      title: t("home.collTowels"),
      subtitle: t("home.collTowelsSub"),
      description: isRTL ? "جودة فندقية فاخرة لمنزلك" : "Hotel-quality for your home",
      color: "#b8a898",
      link: "/collections/towels",
    },
    {
      id: "bathrobes",
      title: t("home.collRobes"),
      subtitle: t("home.collRobesSub"),
      description: isRTL ? "راحة وأناقة في كل صباح" : "Premium comfort every morning",
      color: "#8a7a6a",
      link: "/collections/bathrobes",
    },
  ];

  useEffect(() => {
    let cancelled = false;
    productsApi
      .fetchBestSellingProducts(8, 30)
      .then((list) => {
        if (!cancelled && Array.isArray(list) && list.length > 0) {
          setFeatured(list.map((p) => normalizeApiProduct(p, isRTL)));
        } else if (!cancelled) {
          // Fallback to general products if empty
          productsApi
            .fetchProducts()
            .then((allList) => {
              if (!cancelled && Array.isArray(allList) && allList.length > 0) {
                setFeatured(allList.slice(0, 8).map((p) => normalizeApiProduct(p, isRTL)));
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        // Fallback to fetchProducts
        productsApi
          .fetchProducts()
          .then((allList) => {
            if (!cancelled && Array.isArray(allList) && allList.length > 0) {
              setFeatured(allList.slice(0, 8).map((p) => normalizeApiProduct(p, isRTL)));
            }
          })
          .catch(() => {});
      });

    productsApi
      .fetchCollections()
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setDbCollections(data.filter(c => c.isEnabled).slice(0, 3));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isRTL]);

  useEffect(() => {
    updateSEO({
      title: t("home.metaTitle"),
      description: isRTL
        ? "متجر تيري لاند: برانص حمام فاخرة، فوط وبشاكير فندقية، وتجهيزات جهاز العروسة بخامات القطن والمايكروفايبر وخامة الوجهين. شحن سريع ودفع عند الاستلام."
        : "Terry Land: Luxury bathrobes, hotel towels, bath sheets, and bridal linens crafted from premium cotton & microfiber blends. Fast delivery & cash on delivery.",
      keywords:
        "برانص, برانص حمام, برنس حمام رجالي, برنس حمام حريمي, برنس عرايس, أرواب حمام فاخرة, فوط, فوط حمام, بشاكير, بشكير حمام, أطقم فوط, عرائس, جهاز العروسة, تجهيز عرائس, مفروشات عرايس, أطقم عرايس, مايكروفايبر, قطن, خامة الوجهين, تيري لاند, Terry Land",
      canonical: "https://terryland.net/",
    });
  }, [t, isRTL]);

  return (
    <main className="home">
      <section className="hero">
        <div className="hero__bg" />
        <div className="hero__overlay" />
        <div className="hero__content">
          <div className="hero__eyebrow">{t("home.heroEyebrow")}</div>
          <h1 className="hero__title">
            <span className="hero__title-line">{t("home.heroTitleLine1")}</span>
            <span className="hero__title-line hero__title-line--em"><em>{t("home.heroTitleLine2")}</em></span>
            <span className="sr-only">
              {isRTL
                ? " — تيري لاند | برانص حمام وفوط وبشاكير ومفروشات عرائس فاخرة — قطن ومايكروفايبر"
                : " — Terry Land | Luxury Bathrobes, Towels & Bridal Linens — Cotton & Microfiber"}
            </span>
          </h1>
          <p className="hero__sub" style={{ whiteSpace: "pre-line" }}>
            {t("home.heroSub")}
          </p>
          <div className="hero__ctas">
            <Link to="/shop" className="btn-primary">
              {t("action.shopNow")} <ArrowRight size={15} className="rtl-flip" />
            </Link>
            <Link to="/collections" className="btn-ghost hero__ghost">
              {t("nav.allCollections")}
            </Link>
          </div>
        </div>
        <div className="hero__scroll">
          <span>{t("home.scroll")}</span>
          <div className="hero__scroll-line" />
        </div>
      </section>

      <section className="features-bar">
        <h2 className="sr-only">Why Choose Terry Land Textiles</h2>
        <div className="container features-bar__inner">
          {[
            { icon: Award, title: t("feature.premium"), sub: t("feature.premiumSub") },
            { icon: Sparkles, title: t("feature.ultraSoft"), sub: t("feature.ultraSoftSub") },
            { icon: Layers, title: t("feature.timeless"), sub: t("feature.timelessSub") },
            { icon: Leaf, title: t("feature.sustainable"), sub: t("feature.sustainableSub") },
          ].map((f) => (
            <div key={f.title} className="features-bar__item">
              <f.icon size={20} strokeWidth={1.5} />
              <div>
                <h3 className="features-bar__title">{f.title}</h3>
                <span className="features-bar__sub">{f.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="home-collections">
        <div className="container">
          <div className="home-collections__header">
            <p className="section-label">{t("home.curatedLabel")}</p>
            <h2 className="section-title">{t("home.curatedTitle")}</h2>
            <p className="home-collections__desc">
              {t("home.curatedDesc")}
            </p>
            <Link to="/collections" className="btn-outline" style={{ marginTop: 20 }}>
              {t("action.explore")} <ArrowRight size={14} className="rtl-flip" />
            </Link>
          </div>
          <div className="home-collections__grid">
            {dbCollections.length > 0 ? (
              dbCollections.map((c) => (
                <CollectionCard key={c.id} collection={c} />
              ))
            ) : (
              collections.map((c) => (
                <CollectionCard key={c.id} collection={c} />
              ))
            )}
          </div>
        </div>
      </section>

      <section className="home-products">
        <div className="container">
          <div className="home-products__header">
            <p className="section-label">{t("home.bestsellingLabel")}</p>
            <h2 className="section-title">{t("home.bestsellingTitle")}</h2>
          </div>
          <div className="home-products__grid">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
            ))}
          </div>
          <div className="home-products__cta">
            <Link to="/shop" className="btn-outline">
              {t("home.viewAllProducts")} <ArrowRight size={14} className="rtl-flip" />
            </Link>
          </div>
        </div>
      </section>

      <section className="crafted">
        <div className="container crafted__inner">
          <div className="crafted__content">
            <p className="section-label">{t("home.craftedLabel")}</p>
            <h2 className="section-title">{t("home.craftedTitle")}</h2>
            <p className="crafted__text">
              {t("home.craftedText1")}
            </p>
            <p className="crafted__text">
              {t("home.craftedText2")}
            </p>
            <Link to="/about" className="btn-primary" style={{ marginTop: 8 }}>
              {t("home.ourStory")} <ArrowRight size={15} className="rtl-flip" />
            </Link>
          </div>
          <div className="crafted__visual">
            <div className="crafted__stat">
              <StatCounter target={25} suffix="+" duration={1800} />
              <span className="crafted__label">{t("home.statYears")}</span>
            </div>
            <div className="crafted__stat">
              <StatCounter target={50} suffix="k+" duration={2000} />
              <span className="crafted__label">{t("home.statCustomers")}</span>
            </div>
            <div className="crafted__stat">
              <StatCounter target={100} suffix="%" duration={2200} />
              <span className="crafted__label">{t("home.statCotton")}</span>
            </div>
            <div className="crafted__stat">
              <StatCounter target={4.9} suffix="★" decimals={1} duration={2000} />
              <span className="crafted__label">{t("home.statRating")}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="luxury-banner">
        <div className="luxury-banner__content">
          <p className="section-label luxury-banner__eyebrow">
            {isRTL ? "تجربة استثنائية" : "Experience"}
          </p>
          <h2 className="luxury-banner__title">{t("shop.luxuryBannerTitle")}</h2>
          <p className="luxury-banner__sub">
            {t("shop.luxuryBannerSub")}
          </p>
          <Link to="/shop" className="btn-primary">
            {t("action.shopNow")} <ArrowRight size={15} className="rtl-flip" />
          </Link>
        </div>
      </section>
    </main>
  );
}

function CollectionCard({ collection }) {
  const { isRTL } = useLanguage();
  const title = (isRTL && collection.arabicName) ? collection.arabicName : (collection.title || collection.name);
  const subtitle = (isRTL && collection.arabicSubtitle) ? collection.arabicSubtitle : (collection.subtitle || (isRTL ? "مجموعة فاخرة" : "Premium Collection"));
  const targetParam = collection.name || collection.id || collection.title || "";
  const link = collection.link || `/shop?collection=${encodeURIComponent(targetParam)}`;
  const bg = collection.imageUrl 
    ? `url(${resolveImageUrl(collection.imageUrl)}) center/cover` 
    : (collection.color || "#d4c4a8");

  return (
    <Link to={link} className="collection-card">
      <div className="collection-card__image" style={{ background: bg }}>
        {!collection.imageUrl && <CollectionIllustration id={collection.id} />}
      </div>
      <div className="collection-card__overlay">
        <p className="section-label" style={{ color: "rgba(255,255,255,0.7)" }}>
          {subtitle}
        </p>
        <h3 className="collection-card__title">{title}</h3>
        <span className="collection-card__link btn-ghost" style={{ color: "#fff" }}>
          {isRTL ? "تسوق الآن" : "Shop Now"} <ArrowRight size={13} className="rtl-flip" />
        </span>
      </div>
    </Link>
  );
}

function CollectionIllustration({ id }) {
  if (id === "baby") {
    return (
      <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
        <circle cx="100" cy="85" r="40" fill="rgba(255,255,255,0.15)" />
        <circle cx="100" cy="80" r="28" fill="rgba(255,255,255,0.25)" />
        <circle cx="91" cy="77" r="3" fill="rgba(255,255,255,0.7)" />
        <circle cx="109" cy="77" r="3" fill="rgba(255,255,255,0.7)" />
        <path
          d="M91 88 Q100 94 109 88"
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
        <path d="M72 65 Q65 55 72 50 Q80 60 78 68" fill="rgba(255,255,255,0.2)" />
        <path d="M128 65 Q135 55 128 50 Q120 60 122 68" fill="rgba(255,255,255,0.2)" />
        <path d="M68 110 Q100 105 132 110 L128 160 Q100 165 72 160 Z" fill="rgba(255,255,255,0.15)" />
      </svg>
    );
  }
  if (id === "towels") {
    return (
      <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
        {[60, 75, 90, 105].map((y, i) => (
          <rect
            key={i}
            x="60"
            y={y}
            width="80"
            height="12"
            rx="6"
            fill={`rgba(255,255,255,${0.1 + i * 0.06})`}
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="0.5"
          />
        ))}
        <line x1="60" y1="72" x2="140" y2="72" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <rect x="55" y="55" width="90" height="90" rx="4" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
      <path d="M75 70 L60 170 L140 170 L125 70 Z" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
      <path d="M100 70 L85 100 L90 170 L100 170 Z" fill="rgba(255,255,255,0.1)" />
      <path d="M100 70 L115 100 L110 170 L100 170 Z" fill="rgba(255,255,255,0.1)" />
      <path d="M75 70 L50 130 L65 140 L80 80 Z" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <path d="M125 70 L150 130 L135 140 L120 80 Z" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <rect x="80" y="128" width="40" height="7" rx="1" fill="rgba(255,255,255,0.3)" />
    </svg>
  );
}

function StatCounter({ target, suffix = "", decimals = 0, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setCount(target);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animatedRef.current) {
            animatedRef.current = true;
            let startTimestamp = null;
            const startVal = 0;

            const step = (timestamp) => {
              if (!startTimestamp) startTimestamp = timestamp;
              const elapsed = timestamp - startTimestamp;
              const progress = Math.min(elapsed / duration, 1);
              // Smooth cubic deceleration
              const ease = 1 - Math.pow(1 - progress, 3);
              const currentVal = startVal + (target - startVal) * ease;
              setCount(currentVal);

              if (progress < 1) {
                requestAnimationFrame(step);
              } else {
                setCount(target);
              }
            };

            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [target, duration]);

  const displayVal = decimals > 0 ? count.toFixed(decimals) : Math.round(count);

  return (
    <span ref={elementRef} className="crafted__num">
      {displayVal}
      {suffix}
    </span>
  );
}
